import React, { useState, useMemo } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { COLUNAS_KANBAN } from './constants';
import { TarefaCard } from './TarefaCard';
import { ConclusaoCard } from './ConclusaoCard';
import { Filter, X, FileDown } from 'lucide-react';

export function KanbanBoard({
  tarefasPorEstado, showArquivadas,
  dragOverColumn, onDragOver, onDragLeave, onDrop,
  draggedTarefa, onDragStart, onDragEnd,
  categoriasTarefas, operadores,
  onDuplicate, onEdit, onArchive, onUnarchive, onDelete,
  onConclusaoUpdated,
}) {
  const [filtroOperadorConc, setFiltroOperadorConc] = useState('');
  const [filtroTarefaConc, setFiltroTarefaConc] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');

  const conclusoesFiltradas = useMemo(() => {
    const items = tarefasPorEstado['conclusoes'] || [];
    return items.filter(c => {
      if (filtroOperadorConc && c.operador_id !== filtroOperadorConc) return false;
      if (filtroTarefaConc && !c.tarefa_nome?.toLowerCase().includes(filtroTarefaConc.toLowerCase())) return false;
      if (filtroDataInicio && c.concluido_em) {
        const concDate = c.concluido_em.substring(0, 10);
        if (concDate < filtroDataInicio) return false;
      }
      if (filtroDataFim && c.concluido_em) {
        const concDate = c.concluido_em.substring(0, 10);
        if (concDate > filtroDataFim) return false;
      }
      return true;
    });
  }, [tarefasPorEstado, filtroOperadorConc, filtroTarefaConc, filtroDataInicio, filtroDataFim]);

  const hasFilters = filtroOperadorConc || filtroTarefaConc || filtroDataInicio || filtroDataFim;

  // Exportar PDF das conclusões filtradas (respeita filtros aplicados)
  const exportarConclusoesPDF = () => {
    const items = conclusoesFiltradas;
    if (!items.length) return;
    const cats = new Map(categoriasTarefas.map(c => [c.id, c.nome]));
    const ops = new Map((operadores || []).map(o => [o.id, o.nome_completo || o.username]));
    const fmtDateTime = (iso) => {
      if (!iso) return '—';
      const d = new Date(iso);
      return `${d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: '2-digit' })} ${d.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
    };
    const fmtDuracao = (segs) => {
      if (!segs && segs !== 0) return '—';
      const s = Number(segs);
      if (s < 60) return `${s}s`;
      const m = Math.floor(s / 60); const r = s % 60;
      return r > 0 ? `${m}m ${r}s` : `${m}m`;
    };

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 10;
    const dataGeracao = new Date().toLocaleString('pt-PT');

    // Cabeçalho
    doc.setFillColor(30, 41, 59);
    doc.rect(0, 0, pageW, 18, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14); doc.setFont('helvetica', 'bold');
    doc.text('SAMIDEL · Conclusões de Tarefas Pontuais', margin, 11);
    doc.setFontSize(8); doc.setFont('helvetica', 'normal');
    doc.text(`Gerado em ${dataGeracao}`, pageW - margin, 11, { align: 'right' });

    // Linha de filtros aplicados
    let y = 24;
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    doc.text('Filtros aplicados:', margin, y);
    doc.setFont('helvetica', 'normal');
    const filtrosTxt = [
      filtroDataInicio || filtroDataFim ? `Período: ${filtroDataInicio || '...'} a ${filtroDataFim || '...'}` : null,
      filtroOperadorConc ? `Operador: ${ops.get(filtroOperadorConc) || filtroOperadorConc}` : null,
      filtroTarefaConc ? `Tarefa: ${filtroTarefaConc}` : null,
    ].filter(Boolean);
    doc.text(filtrosTxt.length ? filtrosTxt.join('  ·  ') : 'Sem filtros (todas as conclusões)', margin + 28, y);

    // Resumo
    const totalQty = items.reduce((s, c) => s + (c.quantidade || 0), 0);
    const totalSegs = items.reduce((s, c) => s + (c.tempo_segundos || 0), 0);
    const editadas = items.filter(c => c.editado_em).length;
    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.text(`Total: ${items.length} registo(s)`, margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text(`·  Peças: ${totalQty}`, margin + 38, y);
    doc.text(`·  Tempo total: ${fmtDuracao(totalSegs)}`, margin + 70, y);
    doc.text(`·  Editadas: ${editadas}`, margin + 110, y);

    // Tabela
    autoTable(doc, {
      startY: y + 4,
      head: [['Data/Hora', 'Tarefa', 'CAT', 'Operador', 'Qtd', 'Inicial', 'Editada em', 'Início → Fim', 'Tempo', 'Cont.', 'Marca', 'Obs.']],
      body: items.map(c => {
        const isContagem = !!(c.is_contagem ?? (Array.isArray(c.operacoes) && c.operacoes.some(o => o?.is_contagem === true)));
        const inicio = c.inicio_em ? new Date(c.inicio_em).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
        const fim = c.concluido_em ? new Date(c.concluido_em).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
        return [
          fmtDateTime(c.concluido_em),
          c.tarefa_nome || '—',
          cats.get(c.tarefa_categoria_id) || '—',
          c.operador_nome || '—',
          String(c.quantidade ?? ''),
          c.quantidade_inicial !== undefined && c.quantidade_inicial !== null && c.quantidade_inicial !== c.quantidade ? String(c.quantidade_inicial) : '—',
          c.editado_em ? fmtDateTime(c.editado_em) : '—',
          `${inicio} → ${fim}`,
          fmtDuracao(c.tempo_segundos),
          isContagem ? 'Sim' : '',
          c.marca || '',
          (c.observacoes || '').slice(0, 40)
        ];
      }),
      styles: { fontSize: 7.5, cellPadding: 1.5, overflow: 'linebreak', valign: 'middle' },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: 26 }, 1: { cellWidth: 32 }, 2: { cellWidth: 18 },
        3: { cellWidth: 26 }, 4: { cellWidth: 12, halign: 'center', fontStyle: 'bold' },
        5: { cellWidth: 14, halign: 'center', textColor: [217, 119, 6] },
        6: { cellWidth: 26, fontSize: 7 },
        7: { cellWidth: 32, fontSize: 7 }, 8: { cellWidth: 16, halign: 'center' },
        9: { cellWidth: 12, halign: 'center' }, 10: { cellWidth: 18 }, 11: { cellWidth: 'auto' },
      },
      margin: { left: margin, right: margin },
      didDrawPage: () => {
        const total = doc.getNumberOfPages();
        doc.setFontSize(7); doc.setTextColor(120, 120, 120);
        doc.text(`Página ${doc.getCurrentPageInfo().pageNumber} / ${total}  ·  SAMIDEL · ${dataGeracao}`,
          pageW / 2, doc.internal.pageSize.getHeight() - 5, { align: 'center' });
      },
    });

    const filtroSlug = [
      filtroDataInicio && `de_${filtroDataInicio}`,
      filtroDataFim && `ate_${filtroDataFim}`,
      filtroOperadorConc && 'operador',
      filtroTarefaConc && `tarefa_${filtroTarefaConc}`,
    ].filter(Boolean).join('_');
    const ts = new Date().toISOString().slice(0, 10);
    doc.save(`conclusoes_${filtroSlug || 'todas'}_${ts}.pdf`);
  };

  // Get unique operators from conclusions
  const operadoresConc = useMemo(() => {
    const items = tarefasPorEstado['conclusoes'] || [];
    const map = {};
    items.forEach(c => {
      if (c.operador_id && c.operador_nome) map[c.operador_id] = c.operador_nome;
    });
    return Object.entries(map).map(([id, nome]) => ({ id, nome }));
  }, [tarefasPorEstado]);

  return (
    <div className={`grid gap-4 ${showArquivadas ? 'grid-cols-4' : 'grid-cols-3'}`} data-testid="kanban-board">
      {Object.entries(COLUNAS_KANBAN)
        .filter(([estado]) => showArquivadas || estado !== 'arquivado')
        .map(([estado, config]) => {
          const Icon = config.icon;
          const items = estado === 'conclusoes' ? conclusoesFiltradas : (tarefasPorEstado[estado] || []);
          const totalItems = estado === 'conclusoes' ? (tarefasPorEstado['conclusoes'] || []).length : items.length;
          const isConclusoes = estado === 'conclusoes';

          return (
            <div
              key={estado}
              className={`flex flex-col rounded-xl border-2 ${config.borderColor} ${
                !isConclusoes && dragOverColumn === estado ? 'ring-2 ring-primary ring-offset-2' : ''
              }`}
              onDragOver={!isConclusoes ? (e) => onDragOver(e, estado) : undefined}
              onDragLeave={!isConclusoes ? onDragLeave : undefined}
              onDrop={!isConclusoes ? (e) => onDrop(e, estado) : undefined}
              data-testid={`coluna-${estado}`}
            >
              <div className={`flex items-center justify-between px-4 py-3 ${config.bgLight} rounded-t-lg border-b ${config.borderColor}`}>
                <div className="flex items-center gap-2">
                  <Icon className={`h-5 w-5 ${config.textColor}`} />
                  <span className={`font-semibold ${config.textColor}`}>{config.label}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {isConclusoes && items.length > 0 && (
                    <button
                      onClick={exportarConclusoesPDF}
                      className="flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded bg-white/60 hover:bg-white border border-stone-200 text-stone-700 transition"
                      title={hasFilters ? `Exportar ${items.length} conclusões filtradas para PDF` : `Exportar ${items.length} conclusões para PDF`}
                      data-testid="btn-exportar-conclusoes-pdf"
                    >
                      <FileDown className="h-3 w-3" />
                      <span>PDF</span>
                    </button>
                  )}
                  <Badge variant="secondary" className="text-xs">
                    {isConclusoes && hasFilters ? `${items.length}/${totalItems}` : items.length}
                  </Badge>
                </div>
              </div>

              {/* Filtros para conclusões */}
              {isConclusoes && (
                <div className="px-3 py-2 border-b border-stone-100 bg-white space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Filter className="h-3 w-3 text-stone-400" />
                    <Select value={filtroOperadorConc || "todos"} onValueChange={(v) => setFiltroOperadorConc(v === 'todos' ? '' : v)}>
                      <SelectTrigger className="h-7 text-xs flex-1">
                        <SelectValue placeholder="Operador" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos operadores</SelectItem>
                        {operadoresConc.map(op => (
                          <SelectItem key={op.id} value={op.id}>{op.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {hasFilters && (
                      <button onClick={() => { setFiltroOperadorConc(''); setFiltroTarefaConc(''); setFiltroDataInicio(''); setFiltroDataFim(''); }}
                        className="p-1 text-stone-400 hover:text-red-500" title="Limpar filtros">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <Input placeholder="Filtrar tarefa..." value={filtroTarefaConc}
                    onChange={(e) => setFiltroTarefaConc(e.target.value)} className="h-7 text-xs" />
                  <div className="flex gap-1.5">
                    <Input type="date" value={filtroDataInicio} onChange={(e) => setFiltroDataInicio(e.target.value)} className="h-7 text-xs flex-1" title="Data início" />
                    <Input type="date" value={filtroDataFim} onChange={(e) => setFiltroDataFim(e.target.value)} className="h-7 text-xs flex-1" title="Data fim" />
                  </div>
                </div>
              )}

              <div className="flex-1 p-3 space-y-3 min-h-[400px] bg-stone-50/50 max-h-[600px] overflow-y-auto">
                {items.length === 0 ? (
                  <div className="text-center text-stone-400 text-sm py-8">
                    {isConclusoes ? (hasFilters ? 'Sem resultados para os filtros' : 'Sem conclusoes') : 'Sem tarefas'}
                  </div>
                ) : isConclusoes ? (
                  items.map(c => (
                    <ConclusaoCard key={c.id} conclusao={c} categoriasTarefas={categoriasTarefas} onUpdated={onConclusaoUpdated} />
                  ))
                ) : (
                  items.map(t => (
                    <TarefaCard
                      key={t.id} tarefa={t} categoriasTarefas={categoriasTarefas}
                      draggedTarefa={draggedTarefa} onDragStart={onDragStart} onDragEnd={onDragEnd}
                      onDuplicate={onDuplicate} onEdit={onEdit} onArchive={onArchive}
                      onUnarchive={onUnarchive} onDelete={onDelete}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
    </div>
  );
}
